/**
 * Service Worker registration + periodic update, with every rejection handled
 * (OPS-1034).
 *
 * The bug this exists to fix: the hourly `registration.update()` tick used to be
 * fired inside `setInterval` with NO rejection handler. `update()` re-fetches
 * `/service-worker.js` over the network, so any transient blip — the user is
 * offline or on a captive portal, the tab wakes from sleep, or a Netlify deploy
 * swaps the file mid-fetch — rejects with
 * `TypeError: Failed to update a ServiceWorker for scope (...) with script (...):
 * An unknown error occurred when fetching the script.`
 * That became an UNHANDLED rejection, which `window.onunhandledrejection` routes
 * to BOTH Sentry (→ Errors→Linear auto-files a Triage card) and the error-report
 * relay. One offline moment on one user's tab = one Linear bug card for a
 * condition that is not an app defect and that the next tick self-heals.
 *
 * Fixed at the SOURCE rather than via `Sentry.init({ ignoreErrors })`: the
 * rejection is caught where it happens, so it never becomes an unhandled
 * rejection for ANY sink (Sentry, the relay, or a future one) and no
 * message-substring filter has to be maintained. Compare `TRANSIENT_NETWORK_ERRORS`
 * in `sentry-capture.js` (OPS-688), which is the right tool only for rejections
 * thrown by code we do not own the call site of.
 *
 * A failed update is logged to Better Stack at WARN via the shared logger — that
 * log source does NOT feed the Errors→Linear rule (only the Sentry SDK does), so
 * the failure stays diagnosable without ticketing. `console.*` is deliberately
 * not used: console output never reaches Better Stack.
 *
 * Injectable deps (`container` / `schedule`) exist so this is unit-testable in
 * jsdom, where `navigator.serviceWorker` is undefined.
 */

import { logInfo, logWarn, serializeError } from './betterstack-logger'

/** How often to re-check for a new Service Worker build. */
export const SW_UPDATE_INTERVAL_MS = 60 * 60 * 1000 // 1 hour

/**
 * Run one update check, swallowing any failure.
 *
 * `update()` can both REJECT (the normal network-failure path) and THROW
 * synchronously (e.g. the registration was unregistered underneath us), so both
 * are handled — a fire-and-forget timer callback must never be able to surface
 * either one.
 *
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<void>} always resolves
 */
export function checkForServiceWorkerUpdate(registration) {
  try {
    return Promise.resolve(registration.update()).then(
      () => undefined,
      (error) => {
        logWarn('service-worker-update-failed', serializeError(error))
      }
    )
  } catch (error) {
    logWarn('service-worker-update-failed', serializeError(error))
    return Promise.resolve()
  }
}

/**
 * Register the Service Worker and schedule periodic update checks.
 *
 * Never rejects: a browser without Service Worker support, a registration
 * failure and an update failure all resolve quietly.
 *
 * @param {object} [options]
 * @param {ServiceWorkerContainer} [options.container] - defaults to `navigator.serviceWorker`
 * @param {string} [options.scriptUrl]
 * @param {number} [options.updateIntervalMs]
 * @param {(fn: () => void, ms: number) => unknown} [options.schedule] - defaults to `setInterval`
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export function registerServiceWorker(options = {}) {
  const {
    container = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
    scriptUrl = '/service-worker.js',
    updateIntervalMs = SW_UPDATE_INTERVAL_MS,
    schedule = (fn, ms) => setInterval(fn, ms),
  } = options

  if (!container || typeof container.register !== 'function') {
    return Promise.resolve(null)
  }

  return container
    .register(scriptUrl)
    .then((registration) => {
      logInfo('service-worker-registered', { scope: registration?.scope })
      if (registration && typeof registration.update === 'function') {
        schedule(() => {
          checkForServiceWorkerUpdate(registration)
        }, updateIntervalMs)
      }
      return registration ?? null
    })
    .catch((error) => {
      logWarn('service-worker-registration-failed', serializeError(error))
      return null
    })
}

export default registerServiceWorker
